import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import '../styles/pages/PrivacyPage.css';

const LGPDPage = () => {
  return (
    <>
      <Helmet>
        <title>Conformidade LGPD — LifeUp</title>
        <meta
          name="description"
          content="Como a LifeUp cumpre a Lei Geral de Proteção de Dados (LGPD) — princípios, bases legais, direitos do titular e contato com nosso DPO."
        />
        <link rel="canonical" href="https://lifyup.com.br/lgpd" />
      </Helmet>

      <div className="legal-page">
        <Header />

        <main className="legal-content">
          <div className="container">
            <header className="legal-header">
              <h1 className="legal-title">Conformidade com a LGPD</h1>
              <p className="legal-updated">Lei Geral de Proteção de Dados — Lei nº 13.709/2018</p>
              <p className="legal-updated">Versão 2.0 — vigente a partir de 1º de junho de 2026</p>
            </header>

            <nav className="legal-toc" aria-label="Sumário">
              <h3>Sumário</h3>
              <ol>
                <li><a href="#sobre-lgpd">Sobre a LGPD</a></li>
                <li><a href="#controlador">Controlador e DPO</a></li>
                <li><a href="#principios">Princípios de Tratamento</a></li>
                <li><a href="#bases-legais">Bases Legais Utilizadas</a></li>
                <li><a href="#dados-sensiveis">Dados Pessoais Sensíveis</a></li>
                <li><a href="#direitos">Seus Direitos como Titular</a></li>
                <li><a href="#exercer">Como Exercer seus Direitos</a></li>
                <li><a href="#prazos">Prazos de Resposta</a></li>
                <li><a href="#compartilhamento">Compartilhamento e Operadores</a></li>
                <li><a href="#internacional">Transferência Internacional</a></li>
                <li><a href="#seguranca">Segurança da Informação</a></li>
                <li><a href="#incidentes">Incidentes de Segurança</a></li>
                <li><a href="#anpd">Reclamação à ANPD</a></li>
                <li><a href="#contato">Canais de Contato</a></li>
              </ol>
            </nav>

            <section id="sobre-lgpd" className="legal-section">
              <h2>1. Sobre a LGPD</h2>
              <p>
                A <strong>Lei Geral de Proteção de Dados Pessoais (LGPD — Lei nº 13.709/2018)</strong> é a
                legislação brasileira que regula o tratamento de dados pessoais por pessoas físicas e jurídicas,
                de direito público ou privado, com o objetivo de proteger os direitos fundamentais de liberdade,
                privacidade e o livre desenvolvimento da personalidade da pessoa natural.
              </p>
              <p>
                A LifeUp está comprometida em cumprir integralmente a LGPD e adotou medidas técnicas,
                administrativas e organizacionais para garantir que seus dados sejam tratados com transparência,
                segurança e respeito.
              </p>
              <p>
                Esta página complementa nossa <a href="/privacidade">Política de Privacidade</a>, detalhando
                especificamente como nos alinhamos à LGPD e como você pode exercer seus direitos.
              </p>
            </section>

            <section id="controlador" className="legal-section">
              <h2>2. Controlador e Encarregado de Dados (DPO)</h2>
              <p>
                A <strong>LifeUp</strong> atua como <strong>Controladora</strong> dos dados pessoais coletados por
                meio de seus aplicativos e site, sendo responsável pelas decisões sobre o tratamento.
              </p>
              <div className="info-box">
                <p><strong>Encarregado de Proteção de Dados (DPO):</strong> Equipe de Privacidade LifeUp</p>
                <p><strong>E-mail:</strong> <a href="mailto:dpo@lifyup.com.br">dpo@lifyup.com.br</a></p>
                <p><strong>Site:</strong> <a href="https://lifyup.com.br" target="_blank" rel="noopener noreferrer">lifyup.com.br</a></p>
              </div>
            </section>

            <section id="principios" className="legal-section">
              <h2>3. Princípios de Tratamento (Art. 6º LGPD)</h2>
              <p>
                Pautamos toda atividade de tratamento de dados pelos seguintes princípios estabelecidos pela LGPD:
              </p>
              <ul>
                <li><strong>Finalidade:</strong> tratamento para propósitos legítimos, específicos e informados, sem desvio posterior.</li>
                <li><strong>Adequação:</strong> compatibilidade entre o tratamento e as finalidades informadas.</li>
                <li><strong>Necessidade:</strong> limitação ao mínimo necessário, pertinente e proporcional.</li>
                <li><strong>Livre acesso:</strong> consulta facilitada e gratuita aos dados tratados.</li>
                <li><strong>Qualidade dos dados:</strong> exatidão, clareza e atualização conforme a finalidade.</li>
                <li><strong>Transparência:</strong> informações claras, precisas e acessíveis sobre o tratamento.</li>
                <li><strong>Segurança:</strong> medidas técnicas e administrativas contra acesso não autorizado, perda ou destruição.</li>
                <li><strong>Prevenção:</strong> adoção de medidas para prevenir danos em razão do tratamento.</li>
                <li><strong>Não discriminação:</strong> impossibilidade de realização do tratamento para fins discriminatórios ilícitos ou abusivos.</li>
                <li><strong>Responsabilização e prestação de contas:</strong> demonstração da adoção de medidas eficazes capazes de comprovar a observância da LGPD.</li>
              </ul>
            </section>

            <section id="bases-legais" className="legal-section">
              <h2>4. Bases Legais Utilizadas (Art. 7º e 11 LGPD)</h2>
              <p>O tratamento dos seus dados é fundamentado em uma das seguintes bases legais:</p>
              <table className="legal-table">
                <thead>
                  <tr>
                    <th>Base Legal</th>
                    <th>Finalidade na LifeUp</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Execução de contrato</strong> (art. 7º, V)</td>
                    <td>Criação e manutenção da conta, prestação das funcionalidades dos Programas, cobranças.</td>
                  </tr>
                  <tr>
                    <td><strong>Consentimento</strong> (art. 7º, I; art. 11, I)</td>
                    <td>Notificações push, marketing por e-mail, dados sensíveis de saúde (diário emocional, gatilhos).</td>
                  </tr>
                  <tr>
                    <td><strong>Legítimo interesse</strong> (art. 7º, IX)</td>
                    <td>Segurança da plataforma, prevenção a fraudes, melhoria de produto.</td>
                  </tr>
                  <tr>
                    <td><strong>Cumprimento de obrigação legal</strong> (art. 7º, II)</td>
                    <td>Guarda de registros fiscais, atendimento a autoridades, retenção exigida por lei.</td>
                  </tr>
                  <tr>
                    <td><strong>Proteção da vida</strong> (art. 7º, VII; art. 11, II, &quot;f&quot;)</td>
                    <td>Acionamento do botão de emergência e suporte em casos de risco iminente.</td>
                  </tr>
                  <tr>
                    <td><strong>Exercício regular de direitos</strong> (art. 7º, VI)</td>
                    <td>Defesa em processos judiciais, administrativos ou arbitrais.</td>
                  </tr>
                </tbody>
              </table>
            </section>

            <section id="dados-sensiveis" className="legal-section">
              <h2>5. Dados Pessoais Sensíveis (Art. 5º, II)</h2>
              <p>
                A LGPD classifica como <strong>sensíveis</strong> dados sobre origem racial ou étnica, convicção
                religiosa, opinião política, filiação a sindicato, dado referente à <strong>saúde ou à vida
                sexual</strong>, dado genético ou biométrico.
              </p>
              <div className="warning-box">
                <p>
                  <strong>Atenção:</strong> registros de comportamentos compulsivos, gatilhos, recaídas, estados
                  emocionais e participação em programas como o <em>Freedom</em> (relacionado a pornografia)
                  podem ser classificados como dados sensíveis. Por isso:
                </p>
                <ul>
                  <li>Tratamos esses dados <strong>somente mediante consentimento explícito</strong>;</li>
                  <li>Você pode <strong>revogar o consentimento</strong> a qualquer momento;</li>
                  <li>Aplicamos <strong>criptografia</strong> e controles de acesso reforçados;</li>
                  <li>Esses dados <strong>nunca são vendidos</strong>, compartilhados para publicidade direcionada ou repassados a terceiros sem necessidade legal.</li>
                </ul>
              </div>
            </section>

            <section id="direitos" className="legal-section">
              <h2>6. Seus Direitos como Titular (Art. 18 LGPD)</h2>
              <p>Você, titular dos dados, tem direito a:</p>
              <ul>
                <li><strong>Confirmação:</strong> saber se realizamos tratamento de dados seus.</li>
                <li><strong>Acesso:</strong> obter cópia completa dos dados que mantemos sobre você.</li>
                <li><strong>Correção:</strong> solicitar correção de dados incompletos, inexatos ou desatualizados.</li>
                <li><strong>Anonimização, bloqueio ou eliminação</strong> de dados desnecessários, excessivos ou tratados em desconformidade.</li>
                <li><strong>Portabilidade:</strong> receber seus dados em formato estruturado e interoperável (JSON/CSV).</li>
                <li><strong>Eliminação dos dados tratados com consentimento</strong>, exceto nas hipóteses de retenção legal.</li>
                <li><strong>Informação sobre compartilhamentos</strong> com entes públicos e privados.</li>
                <li><strong>Informação sobre a possibilidade de não fornecer consentimento</strong> e suas consequências.</li>
                <li><strong>Revogação do consentimento</strong> a qualquer momento, sem ônus.</li>
                <li><strong>Oposição</strong> ao tratamento realizado com base em outras hipóteses (que não o consentimento), em caso de descumprimento da LGPD.</li>
                <li><strong>Revisão de decisões automatizadas</strong> que afetem seus interesses (art. 20).</li>
              </ul>
            </section>

            <section id="exercer" className="legal-section">
              <h2>7. Como Exercer seus Direitos</h2>
              <p>
                Você pode exercer qualquer um desses direitos enviando solicitação por um dos seguintes canais:
              </p>
              <ul>
                <li>
                  <strong>E-mail ao DPO:</strong>{' '}
                  <a href="mailto:dpo@lifyup.com.br">dpo@lifyup.com.br</a> — informe seu e-mail de cadastro, o
                  direito que deseja exercer e, se aplicável, documento de identidade para validação.
                </li>
                <li>
                  <strong>No aplicativo:</strong> Configurações &gt; Privacidade &gt; Solicitar meus dados / Excluir
                  conta / Revogar consentimentos.
                </li>
                <li>
                  <strong>No site:</strong>{' '}
                  <a href="https://lifyup.com.br/excluir-conta" target="_blank" rel="noopener noreferrer">
                    lifyup.com.br/excluir-conta
                  </a>{' '}— autoatendimento para exclusão de conta.
                </li>
              </ul>
              <p>
                Para garantir a segurança da sua conta, podemos solicitar informações adicionais para confirmar
                sua identidade antes de atender o pedido.
              </p>
            </section>

            <section id="prazos" className="legal-section">
              <h2>8. Prazos de Resposta</h2>
              <p>
                Responderemos suas solicitações em até <strong>15 dias</strong> contados da data do pedido,
                conforme prazo recomendado pela ANPD. Quando uma solicitação for de alta complexidade, poderemos
                informar a necessidade de prazo adicional, dentro dos limites legais.
              </p>
              <p>
                Caso não seja possível atender o pedido (por exemplo, por exigência legal de retenção),
                comunicaremos as razões de fato ou de direito que impedem a adoção da providência.
              </p>
            </section>

            <section id="compartilhamento" className="legal-section">
              <h2>9. Compartilhamento e Operadores</h2>
              <p>
                Para operar a Plataforma, contamos com <strong>operadores</strong> que tratam dados em nosso nome
                mediante contratos com cláusulas de proteção de dados. Os principais operadores e finalidades
                estão descritos em detalhes em nossa <a href="/privacidade">Política de Privacidade</a>, mas em
                resumo:
              </p>
              <ul>
                <li><strong>Hospedagem:</strong> Amazon Web Services (AWS) — Estados Unidos e Brasil.</li>
                <li><strong>Pagamentos:</strong> Stripe, Mercado Pago, Apple App Store, Google Play.</li>
                <li><strong>E-mail transacional:</strong> SendGrid.</li>
                <li><strong>Notificações push:</strong> Firebase Cloud Messaging (Google).</li>
                <li><strong>Monitoramento de erros:</strong> Sentry.</li>
                <li><strong>Analytics:</strong> Google Analytics 4, Meta Pixel (somente no site, mediante consentimento).</li>
              </ul>
              <p>
                Não comercializamos seus dados. Compartilhamentos com autoridades públicas ocorrem somente
                mediante requisição legal ou ordem judicial.
              </p>
            </section>

            <section id="internacional" className="legal-section">
              <h2>10. Transferência Internacional (Art. 33 LGPD)</h2>
              <p>
                Alguns operadores estão sediados fora do Brasil. Quando isso ocorre, garantimos a transferência
                internacional com base nas hipóteses do art. 33 da LGPD, em especial:
              </p>
              <ul>
                <li>Cláusulas contratuais específicas de proteção de dados;</li>
                <li>Países que oferecem grau de proteção adequado;</li>
                <li>Quando necessário para execução do contrato com o titular.</li>
              </ul>
              <p>
                Adotamos salvaguardas técnicas (criptografia em trânsito e em repouso) e organizacionais
                (contratos de processamento, auditoria de fornecedores) compatíveis com os padrões internacionais
                (ISO 27001, SOC 2).
              </p>
            </section>

            <section id="seguranca" className="legal-section">
              <h2>11. Segurança da Informação (Art. 46 LGPD)</h2>
              <p>Adotamos medidas razoáveis e proporcionais para proteger seus dados, incluindo:</p>
              <ul>
                <li>Criptografia <strong>em trânsito</strong> (TLS 1.2+) e <strong>em repouso</strong> (AES-256);</li>
                <li>Hash de senhas com algoritmos seguros (bcrypt/argon2);</li>
                <li>Controle de acesso por função (RBAC) e princípio do menor privilégio;</li>
                <li>Autenticação multifator (MFA) para administradores;</li>
                <li>Logs de auditoria e monitoramento ativo (SIEM);</li>
                <li>Backups criptografados e plano de recuperação de desastres;</li>
                <li>Avaliações periódicas de vulnerabilidades e testes de invasão (pentests);</li>
                <li>Treinamento contínuo da equipe sobre segurança e privacidade.</li>
              </ul>
            </section>

            <section id="incidentes" className="legal-section">
              <h2>12. Incidentes de Segurança (Art. 48 LGPD)</h2>
              <p>
                Em caso de incidente de segurança que possa acarretar risco ou dano relevante aos titulares,
                comunicaremos a <strong>ANPD</strong> e os titulares afetados em prazo razoável, fornecendo no
                mínimo:
              </p>
              <ul>
                <li>Descrição da natureza dos dados afetados;</li>
                <li>Informações sobre os titulares envolvidos;</li>
                <li>Medidas técnicas e de segurança adotadas;</li>
                <li>Riscos relacionados ao incidente;</li>
                <li>Medidas tomadas para reverter ou mitigar efeitos.</li>
              </ul>
            </section>

            <section id="anpd" className="legal-section">
              <h2>13. Reclamação à ANPD</h2>
              <p>
                Caso entenda que seus direitos não foram atendidos adequadamente, você pode peticionar à
                <strong> Autoridade Nacional de Proteção de Dados (ANPD)</strong> por meio do site oficial:{' '}
                <a href="https://www.gov.br/anpd" target="_blank" rel="noopener noreferrer">
                  www.gov.br/anpd
                </a>
                .
              </p>
              <p>
                Recomendamos, antes, entrar em contato conosco para que possamos buscar uma solução amigável e
                ágil.
              </p>
            </section>

            <section id="contato" className="legal-section">
              <h2>14. Canais de Contato</h2>
              <ul>
                <li><strong>DPO / Privacidade:</strong> <a href="mailto:dpo@lifyup.com.br">dpo@lifyup.com.br</a></li>
                <li><strong>Suporte geral:</strong> <a href="mailto:suporte@lifyup.com.br">suporte@lifyup.com.br</a></li>
                <li><strong>Política de Privacidade:</strong> <a href="/privacidade">/privacidade</a></li>
                <li><strong>Termos de Uso:</strong> <a href="/termos">/termos</a></li>
                <li><strong>Cookies:</strong> <a href="/cookies">/cookies</a></li>
              </ul>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default LGPDPage;
